/**
 * POS Promotions & Uplift Analytics Page
 * ======================================
 * 
 * Promotion management and uplift analysis for measuring effectiveness.
 */

import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Tag,
  ArrowLeft,
  Plus,
  Edit2,
  Trash2,
  BarChart3,
  TrendingUp,
  TrendingDown,
  Calendar,
  Percent,
  DollarSign,
  Eye,
  Package,
  ArrowUpRight,
  ArrowDownRight,
  Minus,
} from "lucide-react";
import { Link } from "react-router-dom";

const POSPromotionsPage = () => {
  const { token } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("promotions");
  const [statusFilter, setStatusFilter] = useState("all");
  
  // Promotions
  const [promotions, setPromotions] = useState([]);
  const [showPromoModal, setShowPromoModal] = useState(false);
  const [editingPromo, setEditingPromo] = useState(null);
  const [promoForm, setPromoForm] = useState({
    name: "",
    description: "",
    promotion_type: "percentage",
    discount_value: "",
    display_type: "shelf",
    start_date: "",
    end_date: "",
  });
  
  // Uplift view
  const [selectedPromo, setSelectedPromo] = useState(null);
  const [upliftData, setUpliftData] = useState(null);
  const [loadingUplift, setLoadingUplift] = useState(false);
  
  // A/B Comparison
  const [showCompare, setShowCompare] = useState(false);
  const [compareA, setCompareA] = useState("");
  const [compareB, setCompareB] = useState("");
  const [compareData, setCompareData] = useState(null);
  
  // Summary
  const [summaryData, setSummaryData] = useState(null);
  
  useEffect(() => {
    fetchPromotions();
    fetchSummary();
  }, [statusFilter]);
  
  const fetchPromotions = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/pos/promotions?status=${statusFilter}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setPromotions(await res.json());
      }
    } catch (err) {
      console.error("Error fetching promotions:", err);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchSummary = async () => {
    try {
      const res = await fetch(`${API}/pos/promotions/analytics/summary?period=month`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setSummaryData(await res.json());
      }
    } catch (err) {
      console.error("Error fetching summary:", err);
    }
  };
  
  const fetchUplift = async (promoId) => {
    setLoadingUplift(true);
    try {
      const res = await fetch(`${API}/pos/promotions/${promoId}/uplift`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setUpliftData(await res.json());
      }
    } catch (err) {
      console.error("Error fetching uplift:", err);
    } finally {
      setLoadingUplift(false);
    }
  };
  
  const handleCreatePromo = async () => {
    try {
      const res = await fetch(`${API}/pos/promotions`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          ...promoForm,
          discount_value: parseFloat(promoForm.discount_value)
        })
      });
      
      if (res.ok) {
        toast.success("Promotion created successfully");
        setShowPromoModal(false);
        resetForm();
        fetchPromotions();
      } else {
        toast.error("Failed to create promotion");
      }
    } catch (err) {
      toast.error("Error creating promotion");
    }
  };
  
  const handleUpdatePromo = async () => {
    try {
      const res = await fetch(`${API}/pos/promotions/${editingPromo.promotion_id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          ...promoForm,
          discount_value: parseFloat(promoForm.discount_value)
        })
      });
      
      if (res.ok) {
        toast.success("Promotion updated successfully");
        setShowPromoModal(false);
        setEditingPromo(null);
        resetForm();
        fetchPromotions();
      } else {
        toast.error("Failed to update promotion");
      }
    } catch (err) {
      toast.error("Error updating promotion");
    }
  };
  
  const handleDeletePromo = async (promoId) => {
    if (!confirm("Are you sure you want to delete this promotion?")) return;
    
    try {
      const res = await fetch(`${API}/pos/promotions/${promoId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success("Promotion deleted");
        fetchPromotions();
      } else {
        toast.error("Failed to delete promotion");
      }
    } catch (err) {
      toast.error("Error deleting promotion");
    }
  };
  
  const handleCompare = async () => {
    if (!compareA || !compareB) {
      toast.error("Please select two promotions to compare");
      return;
    }
    
    try {
      const res = await fetch(`${API}/pos/promotions/compare/ab?promo_a=${compareA}&promo_b=${compareB}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        setCompareData(await res.json());
      } else {
        toast.error("Failed to compare promotions");
      }
    } catch (err) {
      toast.error("Error comparing promotions");
    }
  };
  
  const resetForm = () => {
    setPromoForm({
      name: "",
      description: "",
      promotion_type: "percentage",
      discount_value: "",
      display_type: "shelf",
      start_date: "",
      end_date: "",
    });
  };
  
  const openEditPromo = (promo) => {
    setEditingPromo(promo);
    setPromoForm({
      name: promo.name,
      description: promo.description || "",
      promotion_type: promo.promotion_type,
      discount_value: promo.discount_value.toString(),
      display_type: promo.display_type || "shelf",
      start_date: promo.start_date.split("T")[0],
      end_date: promo.end_date.split("T")[0],
    });
    setShowPromoModal(true);
  };
  
  const viewUplift = (promo) => {
    setSelectedPromo(promo);
    setActiveTab("uplift");
    fetchUplift(promo.promotion_id);
  };
  
  const formatPrice = (pence) => `£${(pence / 100).toFixed(2)}`;
  
  const getPromoStatus = (promo) => {
    const now = new Date();
    const start = new Date(promo.start_date);
    const end = new Date(promo.end_date);
    
    if (!promo.is_active) return { label: "Inactive", color: "bg-slate-500/20 text-gray-500" };
    if (now < start) return { label: "Scheduled", color: "bg-blue-500/20 text-blue-400" };
    if (now > end) return { label: "Ended", color: "bg-amber-500/20 text-amber-400" };
    return { label: "Active", color: "bg-emerald-500/20 text-emerald-400" };
  };
  
  const getUpliftColor = (value) => {
    if (value > 10) return "text-emerald-400";
    if (value < -10) return "text-red-400";
    return "text-amber-400";
  };
  
  const UpliftIndicator = ({ value }) => {
    const Icon = value > 0 ? ArrowUpRight : value < 0 ? ArrowDownRight : Minus;
    return (
      <span className={`flex items-center gap-1 ${getUpliftColor(value)}`}>
        <Icon className="h-4 w-4" />
        {value > 0 ? "+" : ""}{value}%
      </span>
    );
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white text-gray-900">
      {/* Header */}
      <div className="border-b border-gray-200 bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-500 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/merchant/pos" className="text-white/80 hover:text-white">
              <ArrowLeft className="h-5 w-5" />
            </Link>
            <div>
              <h1 className="text-xl font-bold flex items-center gap-2 text-white">
                <Tag className="h-6 w-6 text-fuchsia-200" />
                Promotions & Uplift
              </h1>
              <p className="text-white/70 text-sm">
                Manage promotions and analyze their effectiveness
              </p>
            </div>
          </div>
          
          <Button
            onClick={() => {
              setEditingPromo(null);
              resetForm();
              setShowPromoModal(true);
            }}
            className="bg-white text-indigo-700 hover:bg-indigo-50"
            data-testid="create-promo-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            Create Promotion
          </Button>
        </div>
      </div>
      
      {/* Tabs */}
      <div className="border-b border-gray-200 bg-white/30">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex gap-1">
            {[
              { id: "promotions", label: "All Promotions", icon: Tag },
              { id: "uplift", label: "Uplift Analysis", icon: TrendingUp },
              { id: "compare", label: "A/B Compare", icon: BarChart3 },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-3 text-sm flex items-center gap-2 border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? "border-indigo-500 text-gray-900"
                    : "border-transparent text-gray-500 hover:text-gray-900"
                }`}
              >
                <tab.icon className="h-4 w-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>
      
      {/* Content */}
      <div className="max-w-7xl mx-auto p-6">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-indigo-500"></div>
          </div>
        ) : (
          <>
            {/* Promotions Tab */}
            {activeTab === "promotions" && (
              <div className="space-y-6">
                {/* Summary Cards */}
                {summaryData && (
                  <div className="grid grid-cols-4 gap-4">
                    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
                      <p className="text-gray-500 text-sm mb-1">Total Promotions</p>
                      <p className="text-2xl font-bold">{summaryData.summary.total_promotions}</p>
                    </div>
                    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
                      <p className="text-gray-500 text-sm mb-1">Promo Sales (Month)</p>
                      <p className="text-2xl font-bold">{formatPrice(summaryData.summary.total_sales)}</p>
                    </div>
                    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
                      <p className="text-gray-500 text-sm mb-1">Promo Transactions</p>
                      <p className="text-2xl font-bold">{summaryData.summary.total_transactions}</p>
                    </div>
                    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
                      <p className="text-gray-500 text-sm mb-1">Avg Sales/Promo</p>
                      <p className="text-2xl font-bold">{formatPrice(summaryData.summary.avg_sales_per_promo)}</p>
                    </div>
                  </div>
                )}
                
                {/* Filters */}
                <div className="flex items-center gap-4">
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-40 bg-gray-100 border-gray-200">
                      <SelectValue placeholder="Filter by status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="scheduled">Scheduled</SelectItem>
                      <SelectItem value="ended">Ended</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Promotions List */}
                {promotions.length === 0 ? (
                  <div className="text-center py-12 bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-500 rounded-xl border border-gray-200">
                    <Tag className="h-12 w-12 mx-auto text-slate-600 mb-4" />
                    <p className="text-gray-500 mb-4">No promotions yet.</p>
                    <Button
                      onClick={() => setShowPromoModal(true)}
                      variant="outline"
                      className="border-gray-200"
                    >
                      Create Your First Promotion
                    </Button>
                  </div>
                ) : (
                  <div className="grid gap-4">
                    {promotions.map(promo => {
                      const status = getPromoStatus(promo);
                      return (
                        <div key={promo.promotion_id} className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <h3 className="font-semibold text-lg">{promo.name}</h3>
                                <span className={`px-2 py-0.5 rounded text-xs ${status.color}`}>
                                  {status.label}
                                </span>
                              </div>
                              {promo.description && (
                                <p className="text-sm text-gray-500 mb-3">{promo.description}</p>
                              )}
                              <div className="flex items-center gap-6 text-sm">
                                <span className="flex items-center gap-1 text-gray-500">
                                  <Percent className="h-4 w-4" />
                                  {promo.promotion_type === "percentage" 
                                    ? `${promo.discount_value}% off` 
                                    : formatPrice(promo.discount_value * 100)}
                                </span>
                                <span className="flex items-center gap-1 text-gray-500">
                                  <Eye className="h-4 w-4" />
                                  {promo.display_type || "No display"}
                                </span>
                                <span className="flex items-center gap-1 text-gray-500">
                                  <Calendar className="h-4 w-4" />
                                  {promo.start_date.split("T")[0]} → {promo.end_date.split("T")[0]}
                                </span>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => viewUplift(promo)}
                                className="border-gray-200"
                              >
                                <BarChart3 className="h-4 w-4 mr-1" />
                                View Uplift
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openEditPromo(promo)}
                                className="text-gray-500 hover:text-gray-900"
                              >
                                <Edit2 className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeletePromo(promo.promotion_id)}
                                className="text-gray-500 hover:text-red-400"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
            
            {/* Uplift Tab */}
            {activeTab === "uplift" && (
              <div className="space-y-6">
                {!selectedPromo ? (
                  <div className="text-center py-12 bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-500 rounded-xl border border-gray-200">
                    <TrendingUp className="h-12 w-12 mx-auto text-slate-600 mb-4" />
                    <p className="text-gray-500">Select a promotion to view uplift analysis</p>
                  </div>
                ) : loadingUplift ? (
                  <div className="flex items-center justify-center py-20">
                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-indigo-500"></div>
                  </div>
                ) : upliftData ? (
                  <>
                    <div className="flex items-center justify-between">
                      <div>
                        <h2 className="text-xl font-semibold">{upliftData.promotion.name}</h2>
                        <p className="text-gray-500 text-sm">
                          {upliftData.period.start.split("T")[0]} → {upliftData.period.end.split("T")[0]}
                        </p>
                      </div>
                      <Button
                        variant="outline"
                        onClick={() => {
                          setSelectedPromo(null);
                          setUpliftData(null);
                        }}
                        className="border-gray-200"
                      >
                        Select Different Promotion
                      </Button>
                    </div>
                    
                    {/* Uplift Metrics */}
                    <div className="grid grid-cols-4 gap-4">
                      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
                        <p className="text-gray-500 text-sm mb-1">Sales Uplift</p>
                        <div className="flex items-center gap-2">
                          <p className="text-2xl font-bold">
                            <UpliftIndicator value={upliftData.uplift.sales_uplift_percent} />
                          </p>
                        </div>
                      </div>
                      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
                        <p className="text-gray-500 text-sm mb-1">Transaction Uplift</p>
                        <p className="text-2xl font-bold">
                          <UpliftIndicator value={upliftData.uplift.transaction_uplift_percent} />
                        </p>
                      </div>
                      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
                        <p className="text-gray-500 text-sm mb-1">Items Uplift</p>
                        <p className="text-2xl font-bold">
                          <UpliftIndicator value={upliftData.uplift.items_uplift_percent} />
                        </p>
                      </div>
                      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
                        <p className="text-gray-500 text-sm mb-1">Avg Basket Uplift</p>
                        <p className="text-2xl font-bold">
                          <UpliftIndicator value={upliftData.uplift.basket_uplift_percent} />
                        </p>
                      </div>
                    </div>
                    
                    {/* Period Comparison */}
                    <div className="grid grid-cols-2 gap-6">
                      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
                        <h3 className="font-semibold mb-4 flex items-center gap-2">
                          <TrendingUp className="h-5 w-5 text-emerald-400" />
                          Promotion Period
                        </h3>
                        <div className="space-y-3">
                          <div className="flex justify-between">
                            <span className="text-gray-500">Total Sales</span>
                            <span className="font-medium">{formatPrice(upliftData.promo_period.total_sales)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-500">Transactions</span>
                            <span className="font-medium">{upliftData.promo_period.transaction_count}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-500">Items Sold</span>
                            <span className="font-medium">{upliftData.promo_period.items_sold}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-500">Avg Basket</span>
                            <span className="font-medium">{formatPrice(upliftData.promo_period.avg_basket)}</span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
                        <h3 className="font-semibold mb-4 flex items-center gap-2">
                          <BarChart3 className="h-5 w-5 text-gray-500" />
                          Baseline Period
                        </h3>
                        <div className="space-y-3">
                          <div className="flex justify-between">
                            <span className="text-gray-500">Total Sales</span>
                            <span className="font-medium">{formatPrice(upliftData.baseline_period.total_sales)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-500">Transactions</span>
                            <span className="font-medium">{upliftData.baseline_period.transaction_count}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-500">Items Sold</span>
                            <span className="font-medium">{upliftData.baseline_period.items_sold}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-500">Avg Basket</span>
                            <span className="font-medium">{formatPrice(upliftData.baseline_period.avg_basket)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                ) : null}
              </div>
            )}
            
            {/* A/B Compare Tab */}
            {activeTab === "compare" && (
              <div className="space-y-6">
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
                  <h3 className="font-semibold mb-4">Select Promotions to Compare</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm text-gray-500 mb-1 block">Promotion A</label>
                      <Select value={compareA} onValueChange={setCompareA}>
                        <SelectTrigger className="bg-gray-100 border-gray-200">
                          <SelectValue placeholder="Select promotion" />
                        </SelectTrigger>
                        <SelectContent>
                          {promotions.map(p => (
                            <SelectItem key={p.promotion_id} value={p.promotion_id}>
                              {p.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-sm text-gray-500 mb-1 block">Promotion B</label>
                      <Select value={compareB} onValueChange={setCompareB}>
                        <SelectTrigger className="bg-gray-100 border-gray-200">
                          <SelectValue placeholder="Select promotion" />
                        </SelectTrigger>
                        <SelectContent>
                          {promotions.filter(p => p.promotion_id !== compareA).map(p => (
                            <SelectItem key={p.promotion_id} value={p.promotion_id}>
                              {p.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <Button
                    onClick={handleCompare}
                    disabled={!compareA || !compareB}
                    className="mt-4 bg-indigo-600 hover:bg-indigo-700"
                  >
                    Compare Promotions
                  </Button>
                </div>
                
                {compareData && (
                  <div className="grid grid-cols-2 gap-6">
                    {["a", "b"].map(side => {
                      const data = compareData[`promotion_${side}`];
                      const isWinner = (metric) => compareData.comparison[metric] === side.toUpperCase();
                      
                      return (
                        <div key={side} className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
                          <div className="flex items-center justify-between mb-4">
                            <h3 className="font-semibold">{data.name}</h3>
                            <span className="text-xs text-gray-500 uppercase">Promotion {side.toUpperCase()}</span>
                          </div>
                          <div className="space-y-3">
                            <div className="flex justify-between items-center">
                              <span className="text-gray-500">Total Sales</span>
                              <span className={`font-medium ${isWinner("sales_winner") ? "text-emerald-400" : ""}`}>
                                {formatPrice(data.metrics.total_sales)}
                                {isWinner("sales_winner") && " ★"}
                              </span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-gray-500">Transactions</span>
                              <span className={`font-medium ${isWinner("transactions_winner") ? "text-emerald-400" : ""}`}>
                                {data.metrics.transaction_count}
                                {isWinner("transactions_winner") && " ★"}
                              </span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-gray-500">Items Sold</span>
                              <span className={`font-medium ${isWinner("items_winner") ? "text-emerald-400" : ""}`}>
                                {data.metrics.items_sold}
                                {isWinner("items_winner") && " ★"}
                              </span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-gray-500">Avg Basket</span>
                              <span className={`font-medium ${isWinner("basket_winner") ? "text-emerald-400" : ""}`}>
                                {formatPrice(data.metrics.avg_basket)}
                                {isWinner("basket_winner") && " ★"}
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
      
      {/* Create/Edit Promotion Modal */}
      <Dialog open={showPromoModal} onOpenChange={setShowPromoModal}>
        <DialogContent className="bg-white border-gray-200 max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingPromo ? "Edit Promotion" : "Create Promotion"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm text-gray-500 mb-1 block">Promotion Name</label>
              <Input
                value={promoForm.name}
                onChange={(e) => setPromoForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Summer Sale 20% Off"
                className="bg-gray-100 border-gray-200"
                data-testid="promo-name-input"
              />
            </div>
            <div>
              <label className="text-sm text-gray-500 mb-1 block">Description (Optional)</label>
              <Input
                value={promoForm.description}
                onChange={(e) => setPromoForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Brief description"
                className="bg-gray-100 border-gray-200"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-gray-500 mb-1 block">Promotion Type</label>
                <Select value={promoForm.promotion_type} onValueChange={(v) => setPromoForm(prev => ({ ...prev, promotion_type: v }))}>
                  <SelectTrigger className="bg-gray-100 border-gray-200">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">Percentage Off</SelectItem>
                    <SelectItem value="fixed_amount">Fixed Amount Off</SelectItem>
                    <SelectItem value="bogo">Buy One Get One</SelectItem>
                    <SelectItem value="bundle">Bundle Deal</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm text-gray-500 mb-1 block">
                  {promoForm.promotion_type === "percentage" ? "Discount %" : "Discount Amount (£)"}
                </label>
                <Input
                  type="number"
                  value={promoForm.discount_value}
                  onChange={(e) => setPromoForm(prev => ({ ...prev, discount_value: e.target.value }))}
                  placeholder={promoForm.promotion_type === "percentage" ? "e.g., 20" : "e.g., 5.00"}
                  className="bg-gray-100 border-gray-200"
                />
              </div>
            </div>
            <div>
              <label className="text-sm text-gray-500 mb-1 block">Display Type</label>
              <Select value={promoForm.display_type} onValueChange={(v) => setPromoForm(prev => ({ ...prev, display_type: v }))}>
                <SelectTrigger className="bg-gray-100 border-gray-200">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="shelf">Shelf Display</SelectItem>
                  <SelectItem value="endcap">Endcap Display</SelectItem>
                  <SelectItem value="window">Window Display</SelectItem>
                  <SelectItem value="digital">Digital Signage</SelectItem>
                  <SelectItem value="none">No Display</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-gray-500 mb-1 block">Start Date</label>
                <Input
                  type="date"
                  value={promoForm.start_date}
                  onChange={(e) => setPromoForm(prev => ({ ...prev, start_date: e.target.value }))}
                  className="bg-gray-100 border-gray-200"
                />
              </div>
              <div>
                <label className="text-sm text-gray-500 mb-1 block">End Date</label>
                <Input
                  type="date"
                  value={promoForm.end_date}
                  onChange={(e) => setPromoForm(prev => ({ ...prev, end_date: e.target.value }))}
                  className="bg-gray-100 border-gray-200"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPromoModal(false)} className="border-gray-200">
              Cancel
            </Button>
            <Button
              onClick={editingPromo ? handleUpdatePromo : handleCreatePromo}
              disabled={!promoForm.name || !promoForm.discount_value || !promoForm.start_date || !promoForm.end_date}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="save-promo-btn"
            >
              {editingPromo ? "Update Promotion" : "Create Promotion"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default POSPromotionsPage;
